/*
  based on: https://github.com/rzeigler/waveguide/blob/master/src/semaphore.ts
  credits to original author
 */
import { either as E, function as F, option as O, pipeable as P } from "fp-ts";
import { empty } from "./original/support/dequeue";
import { makeDeferred } from "./deferred";
import { makeRef } from "./ref";
import { makeTicket, ticketExit, ticketUse } from "./ticket";
import * as T from "./effect";
import { effect } from "./effect";
const isReservationFor = (latch) => (rsv) => rsv[1] === latch;
function sanityCheck(n) {
    if (n < 0) {
        return T.raiseAbort(new Error("Die: semaphore permits must be non negative"));
    }
    if (Math.round(n) !== n) {
        return T.raiseAbort(new Error("Die: semaphore permits may not be fractional"));
    }
    return T.unit;
}
function makeSemaphoreImpl(ref) {
    const releaseN = (n) => T.applySecond(sanityCheck(n), T.uninterruptible(n === 0
        ? T.unit
        : T.flatten(ref.modify((current) => P.pipe(current, E.fold((waiting) => P.pipe(waiting.take(), O.fold(() => [T.unit, E.right(n)], ([[needed, latch], q]) => n >= needed
            ? [
                T.applyFirst(latch.done(undefined), n > needed ? releaseN(n - needed) : T.unit),
                E.left(q)
            ]
            : [T.unit, E.left(q.push([needed - n, latch]))])), (ready) => [T.unit, E.right(ready + n)]))))));
    const cancelWait = (n, latch) => T.uninterruptible(T.flatten(ref.modify((current) => P.pipe(current, E.fold((waiting) => P.pipe(waiting.find(isReservationFor(latch)), O.fold(() => [releaseN(n), E.left(waiting)], ([pending]) => [
        releaseN(n - pending),
        E.left(waiting.filter(F.not(isReservationFor(latch))))
    ])), (ready) => [T.unit, E.right(ready + n)])))));
    const ticketN = (n) => effect.chain(makeDeferred(), (latch) => ref.modify((current) => P.pipe(current, E.fold((waiting) => [
        makeTicket(latch.wait, cancelWait(n, latch)),
        E.left(waiting.offer([n, latch]))
    ], (ready) => ready >= n
        ? [makeTicket(T.unit, releaseN(n)), E.right(ready - n)]
        : [
            makeTicket(latch.wait, cancelWait(n, latch)),
            E.left(empty().offer([n - ready, latch]))
        ]))));
    const acquireN = (n) => T.applySecond(sanityCheck(n), n === 0 ? T.unit : T.bracketExit(ticketN(n), ticketExit, ticketUse));
    const withPermitsN = (n, inner) => {
        const acquire = T.interruptible(acquireN(n));
        const release = releaseN(n);
        return T.bracket(acquire, F.constant(release), () => inner);
    };
    const available = effect.map(ref.get, E.fold((q) => -1 * q.size(), F.identity));
    return {
        acquireN,
        acquire: acquireN(1),
        releaseN,
        release: releaseN(1),
        withPermitsN,
        withPermit: (inner) => withPermitsN(1, inner),
        available
    };
}
/**
 * Allocate a semaphore.
 *
 * @param n the number of permits
 * This must be non-negative
 */
export function makeSemaphore(n) {
    return T.applySecond(sanityCheck(n), effect.map(makeRef(E.right(n)), makeSemaphoreImpl));
}
